import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from "recharts";
import { MousePointerClick, Link2, Users, UserCheck } from "lucide-react";

import { AppShell } from "@/components/app/app-shell";
import { Button } from "@/components/ui/button";
import { adminLinkClickAnalytics } from "@/lib/email-clicks.functions";

const description = "See which links in our emails get clicked, who clicked them, and which volunteer or funnel step sent them.";

export const Route = createFileRoute("/_authenticated/t1dwpanel/link-analytics")({
  head: () => ({
    meta: [
      { title: "Email link analytics | Warrior Volunteer" },
      { name: "description", content: description },
      { property: "og:title", content: "Email link analytics | Warrior Volunteer" },
      { property: "og:description", content: description },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: LinkAnalyticsPage,
});

const RANGES = [
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
  { days: 365, label: "12 months" },
];

function shortLabel(value: string, max = 46) {
  const trimmed = value.replace(/^https?:\/\//, "");
  return trimmed.length > max ? `${trimmed.slice(0, max)}...` : trimmed;
}

function LinkAnalyticsPage() {
  const [days, setDays] = useState(90);
  const fetchAnalytics = useServerFn(adminLinkClickAnalytics);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "link-clicks", days],
    queryFn: () => fetchAnalytics({ data: { days } }),
  });

  const totals = data?.totals ?? { clicks: 0, links: 0, recipients: 0, senders: 0 };

  return (
    <AppShell title="Email link analytics" eyebrow="Admin">
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-2">
          {RANGES.map((range) => (
            <Button
              key={range.days}
              size="sm"
              variant={days === range.days ? "default" : "outline"}
              onClick={() => setDays(range.days)}
            >
              {range.label}
            </Button>
          ))}
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Stat icon={<MousePointerClick className="size-4" />} label="Total clicks" value={totals.clicks} />
          <Stat icon={<Link2 className="size-4" />} label="Unique links" value={totals.links} />
          <Stat icon={<Users className="size-4" />} label="People clicking" value={totals.recipients} />
          <Stat icon={<UserCheck className="size-4" />} label="Volunteers with clicks" value={totals.senders} />
        </div>

        {isLoading ? (
          <p className="t-meta text-muted-foreground">Loading click data...</p>
        ) : null}

        <section className="panel p-4">
          <h2 className="t-h3 mb-3">Clicks over time</h2>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data?.byDay ?? []}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <ChartTooltip />
                <Line type="monotone" dataKey="clicks" stroke="var(--primary)" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="panel p-4">
          <h2 className="t-h3 mb-3">Most clicked links</h2>
          {(data?.topLinks ?? []).length === 0 ? (
            <p className="t-meta text-muted-foreground">No clicks recorded yet in this window.</p>
          ) : (
            <>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={(data?.topLinks ?? []).slice(0, 8).map((row) => ({ ...row, name: shortLabel(row.label, 28) }))}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={60} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <ChartTooltip />
                    <Bar dataKey="clicks" fill="var(--primary)" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <SimpleTable
                head={["Link", "Clicks", "People"]}
                rows={(data?.topLinks ?? []).map((row) => [
                  <a key={row.key} href={row.key} target="_blank" rel="noreferrer" className="text-primary underline break-all">
                    {shortLabel(row.label, 70)}
                  </a>,
                  row.clicks,
                  row.people,
                ])}
              />
            </>
          )}
        </section>

        <div className="grid gap-4 lg:grid-cols-2">
          <section className="panel p-4">
            <h2 className="t-h3 mb-3">Who sent the clicked links</h2>
            <SimpleTable
              head={["Sender", "Clicks", "People"]}
              rows={(data?.topSenders ?? []).map((row) => [row.label, row.clicks, row.people])}
            />
          </section>
          <section className="panel p-4">
            <h2 className="t-h3 mb-3">Most engaged recipients</h2>
            <SimpleTable
              head={["Recipient", "Clicks", ""]}
              rows={(data?.topRecipients ?? []).map((row) => [row.label, row.clicks, ""])}
            />
          </section>
        </div>

        <section className="panel p-4">
          <h2 className="t-h3 mb-3">Recent clicks</h2>
          <SimpleTable
            head={["When", "Recipient", "Link", "Source", "Sender"]}
            rows={(data?.recent ?? []).map((row) => [
              new Date(row.clickedAt).toLocaleString(),
              row.recipient ?? "Unknown",
              <a key={row.id} href={row.url} target="_blank" rel="noreferrer" className="text-primary underline break-all">
                {shortLabel(row.url, 46)}
              </a>,
              row.source === "campaign" ? `Invite funnel${row.step != null ? ` (day ${row.step})` : ""}` : row.source,
              row.sender ?? "-",
            ])}
          />
        </section>
      </div>
    </AppShell>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="panel flex items-center gap-3 p-4">
      <span className="text-primary">{icon}</span>
      <div>
        <p className="t-meta text-muted-foreground">{label}</p>
        <p className="t-h2">{value}</p>
      </div>
    </div>
  );
}

function SimpleTable({ head, rows }: { head: string[]; rows: React.ReactNode[][] }) {
  if (rows.length === 0) {
    return <p className="t-meta text-muted-foreground">Nothing here yet.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-muted-foreground">
            {head.map((cell) => (
              <th key={cell} className="t-meta border-b border-border px-2 py-2 font-medium">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-border/60 last:border-0">
              {row.map((cell, j) => (
                <td key={j} className="px-2 py-2 align-top">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
